import { useAuthStore } from '../stores/auth.store.ts';
import { getGreeting, formatFriendlyDate, appointmentTimeText, formatContextualDateConnector } from './date.ts';
import { formatBs, convertToBs, getCurrencySymbol } from './currency.ts';
import { interpolateTemplate } from './templateEngine.ts';
import type { Settings } from '@/types/settings';
import type { Appointment, CampaignClient } from '@/types/appointments';

/**
 * Format customer name respecting nameMode and customerNameMode alias setting.
 */
export function formatClientName(
  fullName: string,
  settings: Settings,
  firstName?: string
): string {
  if (settings.customerNameMode === 'alias') {
    return settings.customerAlias || 'cariño';
  }
  if (settings.nameMode === 'full') {
    return fullName || 'cliente';
  }
  return firstName || fullName?.trim().split(/\s+/)[0] || 'cliente';
}

/**
 * Format price line for WhatsApp messages according to format setting.
 */
export function formatMessagePriceLine(
  kind: 'total' | 'pending',
  originalAmount: number,
  bsAmount: number,
  currencyCode: string,
  format: 'currency' | 'bs' | 'both'
): string {
  const sym = getCurrencySymbol(currencyCode);
  const origText = Number.isFinite(originalAmount) && originalAmount > 0
    ? `${sym}${originalAmount.toFixed(2)}`
    : '';
  const bsText = Number.isFinite(bsAmount) && bsAmount > 0
    ? formatBs(bsAmount)
    : '';

  let valueText = '';
  if (format === 'currency') {
    valueText = origText;
  } else if (format === 'bs') {
    valueText = bsText;
  } else if (origText && bsText) {
    valueText = `${origText} (${bsText})`;
  } else {
    valueText = origText || bsText;
  }

  if (!valueText) return '';
  return kind === 'pending'
    ? `Te queda pendiente ${valueText}.`
    : `El total es ${valueText}.`;
}

/**
 * Build dynamic price text based on appointment status, payment status, and settings.
 */
export function getMessagePriceText(
  appointment: Appointment,
  settings: Settings,
  rate: number = 0
): string {
  if (settings.messagePriceMode === 'off') return '';

  const total = Number(appointment.totalAmount ?? appointment.price ?? 0);
  const status = (appointment.status || '').toLowerCase();
  const paymentStatus = (appointment.salePaymentStatus || '').toLowerCase();
  const isPaid = paymentStatus === 'paid';

  // Ocultar bloque si está pagada al 100% o si fue cancelada / no-show
  if (isPaid || status === 'cancelled' || status === 'no-show') {
    return '';
  }

  let kind: 'total' | 'pending' = 'total';
  if (settings.messagePriceMode === 'pending') {
    kind = 'pending';
  } else if (settings.messagePriceMode === 'auto') {
    if (paymentStatus === 'partially_paid' || status === 'completed') {
      kind = 'pending';
    } else {
      kind = 'total';
    }
  }

  const bsAmount = rate > 0 ? convertToBs(total, rate, settings.recargo) : 0;
  return formatMessagePriceLine(
    kind,
    total,
    bsAmount,
    settings.currencyCode || 'USD',
    settings.messagePriceFormat || 'both'
  );
}

export type AppointmentTemplateKey =
  | 'auto'
  | 'reminder'
  | 'confirmation'
  | 'review'
  | 'followup'
  | 'reschedule';

export interface AppointmentTemplateOption {
  key: AppointmentTemplateKey;
  label: string;
}

export const APPOINTMENT_TEMPLATE_OPTIONS: AppointmentTemplateOption[] = [
  { key: 'auto', label: 'Auto' },
  { key: 'reminder', label: 'Recordatorio' },
  { key: 'confirmation', label: 'Confirmación' },
  { key: 'review', label: 'Reseña' },
  { key: 'followup', label: 'Seguimiento' },
  { key: 'reschedule', label: 'Reagendar' },
];

export function getDefaultTemplateKeyForAppointment(_appointment: Appointment): AppointmentTemplateKey {
  return 'auto';
}

export function resolveAppointmentTemplate(
  appointment: Appointment,
  settings: Settings,
  templateKey?: AppointmentTemplateKey | string
): string {
  if (!templateKey || templateKey === 'auto') {
    return getTemplateForAppointment(appointment, settings);
  }

  const status = (appointment.status || '').toLowerCase().replace('_', '-');

  switch (templateKey) {
    case 'reminder':
      return settings.templateReminder;
    case 'confirmation':
      return settings.templateNew || settings.templateReminder;
    case 'review':
      return settings.templateCompletedReview || settings.templateCompleted;
    case 'followup':
      return settings.templateCompletedGeneral || settings.templateCompleted;
    case 'reschedule':
      if (status === 'cancelled') return settings.templateCancelled || settings.templateReminder;
      if (status === 'no-show') return settings.templateNoShow || settings.templateReminder;
      return settings.templateCompletedRebook || settings.templateCompleted;
    default:
      return templateKey;
  }
}

/**
 * Choose template based on appointment status and completed strategy.
 */
function getTemplateForAppointment(appointment: Appointment, settings: Settings): string {
  const status = (appointment.status || '').toLowerCase().replace('_', '-');

  if (status === 'cancelled') {
    return settings.templateCancelled || settings.templateReminder;
  }
  if (status === 'no-show') {
    return settings.templateNoShow || settings.templateReminder;
  }
  if (status === 'completed') {
    const strat = settings.completedMessageMode || 'auto';
    if (strat === 'review_request') {
      return settings.templateCompletedReview || settings.templateCompleted;
    }
    if (strat === 'rebook') {
      return settings.templateCompletedRebook || settings.templateCompleted;
    }
    if (strat === 'loyalty') {
      return settings.templateCompletedGeneral || settings.templateCompleted;
    }
    // Auto: If review rating is present -> loyalty, otherwise request review
    return appointment.reviewRating
      ? (settings.templateCompletedGeneral || settings.templateCompleted)
      : (settings.templateCompletedReview || settings.templateCompleted);
  }
  if (status === 'new' && settings.templateNew) {
    return settings.templateNew;
  }
  if (status === 'arrived' && settings.templateArrived) {
    return settings.templateArrived;
  }
  if (status === 'started' && settings.templateStarted) {
    return settings.templateStarted;
  }

  return settings.templateReminder || settings.templateNew;
}

/**
 * Build a WhatsApp message for an appointment based on settings and templates.
 */
export function messageForAppointment(
  appointment: Appointment,
  settings: Settings,
  rate: number = 0,
  templateOverride?: AppointmentTemplateKey | string
): string {
  const template = templateOverride
    ? resolveAppointmentTemplate(appointment, settings, templateOverride)
    : getTemplateForAppointment(appointment, settings);
  const clientName = formatClientName(appointment.customerName, settings);

  // Time and Date formatting
  const timeFormatted = appointmentTimeText(appointment.startTime, appointment.endTime, {
    timeMode: settings.timeMode,
    messageTimeMode: settings.messageTimeMode,
    connector: settings.messageRangeConnector,
  });

  const friendlyDate = formatFriendlyDate(appointment.date || '', {
    useFriendlyDate: settings.messageUseFriendlyDate,
    includeDayName: settings.messageIncludeDayName,
    useTomorrowTodayLabels: settings.messageUseTomorrowTodayLabels,
  });

  const dateTimeText = appointment.date ? `${friendlyDate} ${timeFormatted}`.trim() : timeFormatted;

  const paraDateTimeText = appointment.date
    ? formatContextualDateConnector(appointment.date, timeFormatted, 'para', {
        useFriendlyDate: settings.messageUseFriendlyDate,
        includeDayName: settings.messageIncludeDayName,
        useTomorrowTodayLabels: settings.messageUseTomorrowTodayLabels,
      })
    : timeFormatted;

  const elDateTimeText = appointment.date
    ? formatContextualDateConnector(appointment.date, timeFormatted, 'el', {
        useFriendlyDate: settings.messageUseFriendlyDate,
        includeDayName: settings.messageIncludeDayName,
        useTomorrowTodayLabels: settings.messageUseTomorrowTodayLabels,
      })
    : timeFormatted;

  const rawCustomerName = appointment.customerName || '';
  const nameParts = rawCustomerName.trim().split(/\s+/);
  const primerNombre = nameParts[0] || 'Cliente';
  const apellido = nameParts.slice(1).join(' ');
  const nombreCompleto = rawCustomerName || primerNombre;

  const servicesText = settings.showServices && appointment.serviceName ? appointment.serviceName : '';
  const deServices = servicesText ? `de ${servicesText}` : '';

  // Prices
  const sym = getCurrencySymbol(settings.currencyCode);
  const total = Number(appointment.totalAmount ?? appointment.price ?? 0);
  const priceVal = total > 0 ? total.toFixed(2) : '';
  const montoTotal = total > 0 ? `${sym}${total.toFixed(2)}` : '';
  const bsVal = rate > 0 && total > 0
    ? formatBs(convertToBs(total, rate, settings.recargo))
    : '';

  const priceText = getMessagePriceText(appointment, settings, rate);
  const priceBlock = priceText ? `\n\n${priceText}` : '';

  const greeting = getGreeting(
    appointment.date,
    settings.greetingMode,
    settings.greetingPlainPrefix
  );

  const closing = String(settings.closingText || '')
    .trim()
    .replace(/\.\s+/g, '.\n');

  const authState = useAuthStore.getState();
  const businessName = settings.businessName?.trim() || authState.freshaProvider || authState.provider?.data?.attributes?.name || 'Tu Centro / Negocio';

  const reviewBlock = settings.reviewLink?.trim()
    ? `¿Nos dejarías tu opinión? Nos ayuda muchísimo a seguir mejorando:\n👉 ${settings.reviewLink.trim()}`
    : '';

  return interpolateTemplate(template, {
    saludo: greeting,
    nombre: clientName,
    cliente: clientName,
    primer_nombre: primerNombre,
    nombre_completo: nombreCompleto,
    apellido,
    servicios: servicesText,
    de_servicios: deServices,
    fecha: friendlyDate,
    hora: timeFormatted,
    fecha_hora: dateTimeText,
    para_fecha_hora: paraDateTimeText,
    el_fecha_hora: elDateTimeText,
    monto: priceVal,
    monto_total: montoTotal,
    precio_total: montoTotal,
    precio: priceText || priceVal,
    monto_bs: bsVal,
    precio_bs: bsVal,
    precio_bloque: priceBlock,
    negocio: businessName,
    sede: appointment.locationName || 'nuestro centro',
    firma: closing,
    cierre: closing,
    link_resena: reviewBlock,
    enlace_resena: reviewBlock,
    extra: '',
    estilista: appointment.employeeName || 'Nuestro equipo',
    profesional: appointment.employeeName || 'Nuestro equipo',
  });
}

/**
 * Build a WhatsApp message for a campaign client.
 */
export function messageForCampaign(
  client: CampaignClient,
  settings: Settings
): string {
  const templateMap: Record<string, string> = {
    lapsed: settings.msgTemplateLapsed || settings.templateCampaignLapsed,
    vip: settings.msgTemplateVip || settings.templateCampaignVip,
    debtor: settings.msgTemplateDebtor || settings.templateCampaignDebtor,
  };
  const template = templateMap[client.segment] || settings.msgTemplateLapsed || settings.templateCampaignLapsed;
  const clientName = formatClientName(client.name, settings);
  const sym = getCurrencySymbol(settings.currencyCode);

  const greeting = getGreeting(
    undefined,
    settings.greetingMode,
    settings.greetingPlainPrefix
  );

  const closing = String(settings.closingText || '')
    .trim()
    .replace(/\.\s+/g, '.\n');

  const salesFormatted = `${sym}${(client.totalSales || 0).toFixed(2)}`;
  const debtFormatted = `${sym}${(client.outstandingAmount || 0).toFixed(2)}`;

  const authState = useAuthStore.getState();
  const businessName = settings.businessName?.trim() || authState.freshaProvider || authState.provider?.data?.attributes?.name || 'Tu Centro / Negocio';

  return interpolateTemplate(template, {
    saludo: greeting,
    nombre: clientName,
    cliente: clientName,
    dias: String(client.daysAbsent || 0),
    dias_ausente: String(client.daysAbsent || 0),
    total_citas: String(client.totalAppointments || 0),
    total_ventas: salesFormatted,
    monto_ventas: (client.totalSales || 0).toFixed(2),
    deuda: debtFormatted,
    monto_deuda: (client.outstandingAmount || 0).toFixed(2),
    negocio: businessName,
    firma: closing,
    cierre: closing,
    profesional: client.lastStaffMember || 'Nuestro equipo',
    estilista: client.lastStaffMember || 'Nuestro equipo',
  });
}
